import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StatusPill } from '@/components/ui/status-pill'
import { useT } from '@/components/visitone/visitone-provider'
import { cn } from '@/lib/utils'

export type SaveState = 'idle' | 'saving' | 'saved' | 'failed'

/*
 * SaveBar (VIS-321): shown while a configuration form has unsaved changes,
 * with the changed-field count in text. On a page it docks to the bottom of
 * the content region and reserves its height as the region's scroll padding,
 * so a focused field is never hidden behind it; in a slide-over it is the
 * panel footer. One polite live region, always mounted, announces it
 * appearing, the count changing and the save completing.
 */
export function SaveBar({
  count,
  invalidCount = 0,
  state,
  onDiscard,
  onSave,
  docked = 'page',
}: {
  count: number
  /** Fields needing attention after a save attempt. */
  invalidCount?: number
  state: SaveState
  onDiscard: () => void
  onSave: () => void
  docked?: 'page' | 'panel'
}) {
  const t = useT()
  const ref = React.useRef<HTMLDivElement>(null)
  const visible = count > 0 || state === 'saving'

  const message =
    state === 'saving'
      ? t('forms.saveBar.saving')
      : state === 'failed'
        ? t('forms.saveBar.failed', { count })
        : invalidCount > 0
          ? t('forms.saveBar.changedWithAttention', { count, invalid: invalidCount })
          : t('forms.saveBar.changed', { count })

  React.useLayoutEffect(() => {
    const bar = ref.current
    const region = bar?.closest('main')
    if (!visible || docked !== 'page' || !bar || !region) return
    const reserve = () => region.style.setProperty('--vo-save-bar-offset', `calc(${bar.offsetHeight}px + 1rem)`)
    reserve()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserve)
    observer?.observe(bar)
    return () => {
      observer?.disconnect()
      region.style.removeProperty('--vo-save-bar-offset')
    }
  }, [visible, docked])

  return (
    <>
      <p className="sr-only" role="status" aria-live="polite">
        {visible ? message : state === 'saved' ? t('forms.saveBar.saved') : ''}
      </p>
      {visible ? (
        <div
          ref={ref}
          data-slot="save-bar"
          className={cn(
            'flex flex-wrap items-center gap-x-3 gap-y-2 border-line bg-surface px-4 py-3',
            docked === 'page' ? 'sticky bottom-0 z-10 mt-4 rounded-md border shadow-e2' : 'border-t',
          )}
        >
          <div className="flex min-w-0 flex-1 basis-60 items-center gap-2">
            {state === 'saving' ? (
              <Loader2 aria-hidden="true" className="size-4 shrink-0 text-fg-muted motion-safe:animate-spin" />
            ) : (
              <StatusPill tone={state === 'failed' ? 'danger' : 'warning'} className="shrink-0 whitespace-nowrap">
                {state === 'failed' ? t('forms.saveBar.notSaved') : t('forms.saveBar.unsaved')}
              </StatusPill>
            )}
            <p className="min-w-0 text-sm text-fg">{message}</p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="lg" onClick={onDiscard} disabled={state === 'saving'}>
              {t('forms.saveBar.discard')}
            </Button>
            <Button variant="primary" size="lg" onClick={onSave} disabled={state === 'saving'}>
              {state === 'failed' ? t('forms.saveBar.retry') : t('forms.saveBar.save')}
            </Button>
          </div>
        </div>
      ) : state === 'saved' && docked === 'page' ? (
        <p className="mt-4 text-sm font-medium text-success-text">{t('forms.saveBar.saved')}</p>
      ) : null}
    </>
  )
}
